'use client';

import * as DS from '@aurorasolar/ds';
import { Formik, FormikHelpers } from 'formik';
import { type CSSProperties, useCallback, useContext } from 'react';
import * as Yup from 'yup';

import { useLogger, useTracking } from '@/analytics';
import { VisitorContext, getApiHeaders } from '@/context';
import { TRACKING } from '@/helpers/tracking';
import { useToast } from '@/hooks';

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email').required('Email is required'),
});

type FormSchema = Yup.InferType<typeof validationSchema>;

const initialValues: FormSchema = { email: '' };

type SubscribeProps = {
  buttonStyle?: CSSProperties;
  buttonText?: string;
  icon?: DS.IIconComponent;
  iconPosition?: 'left' | 'right';
  variant?: 'cta' | 'primary' | 'secondary' | 'text';
};

export function Subscribe({ buttonStyle, buttonText, icon, iconPosition = 'right', variant = 'cta' }: SubscribeProps) {
  const { addError } = useLogger();
  const { trackEvent } = useTracking();
  const { updateVisitor } = useContext(VisitorContext);
  const { showError, showSuccess } = useToast();

  const handleSubmit = useCallback(
    async ({ email }: FormSchema, { resetForm }: FormikHelpers<FormSchema>) => {
      try {
        await Promise.all([saveSubscription(email), updateVisitor({ email })]);
        trackEvent(TRACKING.BLOG_SUBSCRIBE);
        showSuccess('Subscribed!', 'You will receive updates in your inbox.');
      } catch (error) {
        addError(error, { action: 'handleSubmit', component: 'Subscribe' });
        showError('Failed to subscribe', 'Please try again.');
      }

      resetForm();
    },
    [addError, showError, showSuccess, trackEvent, updateVisitor]
  );

  const inputField = (
    <DS.Field
      component={DS.TextInput}
      icon={DS.IconEnvelope}
      inputProps={{ 'aria-label': 'Email Address', fontSize: '16px' }}
      labelProps={{ w: '100%' }}
      name="email"
      placeholder="Email Address"
      size="md"
    />
  );

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      <DS.Form flexDirection="row" gap={0}>
        {variant === 'cta' ? <DS.LightModeProvider>{inputField}</DS.LightModeProvider> : inputField}
        <DS.Button
          icon={icon}
          iconPosition={iconPosition}
          size="md"
          style={buttonStyle}
          type="submit"
          variant={variant}
        >
          {buttonText}
        </DS.Button>
      </DS.Form>
    </Formik>
  );
}

const saveSubscription = async (email: string) => {
  const url = `/home-solar/api/v1/nurture/subscribe/${encodeURIComponent(email)}`;
  const response = await fetch(url, {
    body: JSON.stringify({ blog_subscription: true }),
    headers: getApiHeaders(),
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }
};
